import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryResult,
  type UseMutationResult,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query';
import postService from '../services/post.service';
import type {
  Post,
  Comment,
  CreatePostData,
  UpdatePostData,
  CreateCommentData,
  PostsResponse,
  CommentsResponse,
  HashtagsResponse,
} from '../types/post';

/**
 * Hook pour récupérer un post par son ID
 */
export const usePost = (id: string): UseQueryResult<{ post: Post }> => {
  return useQuery({
    queryKey: ['post', id],
    queryFn: () => postService.getPost(id),
    enabled: !!id,
  });
};

/**
 * Hook pour récupérer les posts d'un utilisateur
 */
// Le resultat d'une infinite query est un InfiniteData : les consommateurs
// lisent data.pages, pas directement PostsResponse.
export const useUserPosts = (
  userId: string,
  limit = 20
): UseInfiniteQueryResult<InfiniteData<PostsResponse>> => {
  return useInfiniteQuery({
    queryKey: ['userPosts', userId],
    queryFn: ({ pageParam = 0 }) => postService.getUserPosts(userId, { limit, offset: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce((acc, page) => acc + page.posts.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    initialPageParam: 0,
    enabled: !!userId,
  });
};

/**
 * Hook pour récupérer le feed de découverte (infinite scroll)
 */
export const useFeed = (limit = 20): UseInfiniteQueryResult<InfiniteData<PostsResponse>> => {
  return useInfiniteQuery({
    queryKey: ['feed'],
    queryFn: ({ pageParam = 0 }) => postService.getFeed({ limit, offset: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce((acc, page) => acc + page.posts.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    initialPageParam: 0,
  });
};

/**
 * Hook pour créer un post
 */
export const useCreatePost = (): UseMutationResult<
  { message: string; post: Post },
  Error,
  CreatePostData
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePostData) => postService.createPost(data),
    onSuccess: () => {
      // Invalider les caches pour rafraîchir les listes
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

/**
 * Hook pour mettre à jour un post
 */
export const useUpdatePost = (): UseMutationResult<
  { message: string; post: Post },
  Error,
  { id: string; data: UpdatePostData }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => postService.updatePost(id, data),
    onSuccess: (_, variables) => {
      // Invalider le cache du post spécifique et des listes
      queryClient.invalidateQueries({ queryKey: ['post', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

/**
 * Hook pour supprimer un post
 */
export const useDeletePost = (): UseMutationResult<{ message: string }, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => postService.deletePost(id),
    onSuccess: (_, id) => {
      // Supprimer le post du cache et invalider les listes
      queryClient.removeQueries({ queryKey: ['post', id] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

/**
 * Hook pour liker/unliker un post
 */
export const useLikePost = (): UseMutationResult<
  { message: string },
  Error,
  { postId: string; isLiked: boolean }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, isLiked }) =>
      isLiked ? postService.unlikePost(postId) : postService.likePost(postId),
    onSuccess: (_, variables) => {
      // Invalider le cache du post pour rafraîchir les likes
      queryClient.invalidateQueries({ queryKey: ['post', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

/**
 * Hook pour récupérer les commentaires d'un post (infinite scroll)
 */
export const useComments = (
  postId: string,
  limit = 20
): UseInfiniteQueryResult<InfiniteData<CommentsResponse>> => {
  return useInfiniteQuery({
    queryKey: ['comments', postId],
    queryFn: ({ pageParam = 0 }) => postService.getComments(postId, { limit, offset: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce((acc, page) => acc + page.comments.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    initialPageParam: 0,
    enabled: !!postId,
  });
};

/**
 * Hook pour ajouter un commentaire
 */
export const useAddComment = (): UseMutationResult<
  { message: string; comment: Comment },
  Error,
  { postId: string; data: CreateCommentData }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, data }) => postService.addComment(postId, data),
    onSuccess: (_, variables) => {
      // Invalider les commentaires et le post pour rafraîchir le compteur
      queryClient.invalidateQueries({ queryKey: ['comments', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['commentReplies', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['post', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

/**
 * Hook pour supprimer un commentaire
 */
export const useDeleteComment = (): UseMutationResult<
  { message: string },
  Error,
  { postId: string; commentId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, commentId }) => postService.deleteComment(postId, commentId),
    onSuccess: (_, variables) => {
      // Invalider les commentaires et le post pour rafraîchir le compteur
      queryClient.invalidateQueries({ queryKey: ['comments', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['commentReplies', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['post', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

/**
 * Réponses à un commentaire, chargées quand on déplie le fil.
 */
export const useCommentReplies = (
  postId: string,
  commentId: string,
  enabled: boolean
): UseInfiniteQueryResult<InfiniteData<CommentsResponse>> => {
  return useInfiniteQuery({
    queryKey: ['commentReplies', postId, commentId],
    queryFn: ({ pageParam = 0 }) =>
      postService.getReplies(postId, commentId, { limit: 20, offset: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce((acc, page) => acc + page.comments.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    initialPageParam: 0,
    enabled: enabled && !!postId && !!commentId,
  });
};

/** Applique `patch` au commentaire `commentId`, où qu'il soit en cache. */
export const patchCachedComment = (
  queryClient: ReturnType<typeof useQueryClient>,
  postId: string,
  commentId: string,
  patch: (comment: Comment) => Comment
) => {
  const update = (data: InfiniteData<CommentsResponse> | undefined) =>
    data?.pages
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            comments: page.comments.map((c) => (c.id === commentId ? patch(c) : c)),
          })),
        }
      : data;

  queryClient.setQueriesData<InfiniteData<CommentsResponse>>({ queryKey: ['comments', postId] }, update);
  queryClient.setQueriesData<InfiniteData<CommentsResponse>>(
    { queryKey: ['commentReplies', postId] },
    update
  );
};

/**
 * Aimer / ne plus aimer un commentaire. Le cœur change tout de suite ; le
 * compteur définitif vient du serveur.
 */
export const useToggleCommentLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, commentId, isLiked }: { postId: string; commentId: string; isLiked: boolean }) =>
      isLiked ? postService.unlikeComment(postId, commentId) : postService.likeComment(postId, commentId),
    onMutate: ({ postId, commentId, isLiked }) => {
      patchCachedComment(queryClient, postId, commentId, (c) => ({
        ...c,
        userLiked: !isLiked,
        likes_count: Math.max(0, (c.likes_count ?? 0) + (isLiked ? -1 : 1)),
      }));
    },
    onSuccess: ({ likes_count }, { postId, commentId }) => {
      patchCachedComment(queryClient, postId, commentId, (c) => ({ ...c, likes_count }));
    },
    onError: (_error, { postId, commentId, isLiked }) => {
      patchCachedComment(queryClient, postId, commentId, (c) => ({
        ...c,
        userLiked: isLiked,
        likes_count: Math.max(0, (c.likes_count ?? 0) + (isLiked ? 1 : -1)),
      }));
    },
  });
};

/**
 * Fermer / rouvrir la discussion d'un post (auteur uniquement).
 */
export const useSetCommentsDisabled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, disabled }: { postId: string; disabled: boolean }) =>
      postService.updatePost(postId, { comments_disabled: disabled }),
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
      queryClient.invalidateQueries({ queryKey: ['post', postId] });
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
    },
  });
};

/**
 * Mes favoris, du dernier sauvegardé au premier.
 */
export const useSavedPosts = (
  limit = 20,
  enabled = true
): UseInfiniteQueryResult<InfiniteData<PostsResponse>> => {
  return useInfiniteQuery({
    queryKey: ['savedPosts', limit],
    queryFn: ({ pageParam = 0 }) => postService.getSavedPosts({ limit, offset: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce((acc, page) => acc + page.posts.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    initialPageParam: 0,
    enabled,
  });
};

/**
 * Sauvegarder / retirer un post des favoris. Le signet change tout de suite
 * dans toutes les listes ; en cas d'échec, il revient à son état.
 */
export const useToggleSavePost = () => {
  const queryClient = useQueryClient();

  const setSaved = (postId: string, saved: boolean) => {
    const patch = (data: InfiniteData<PostsResponse> | undefined) =>
      data?.pages
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              posts: page.posts.map((p) => (p.id === postId ? { ...p, userSaved: saved } : p)),
            })),
          }
        : data;
    queryClient.setQueriesData<InfiniteData<PostsResponse>>({ queryKey: ['feed'] }, patch);
    queryClient.setQueriesData<InfiniteData<PostsResponse>>({ queryKey: ['userPosts'] }, patch);
    queryClient.setQueriesData<InfiniteData<PostsResponse>>({ queryKey: ['savedPosts'] }, patch);
    queryClient.setQueryData<{ post: Post }>(['post', postId], (data) =>
      data ? { post: { ...data.post, userSaved: saved } } : data
    );
  };

  return useMutation({
    mutationFn: ({ postId, isSaved }: { postId: string; isSaved: boolean }) =>
      isSaved ? postService.unsavePost(postId) : postService.savePost(postId),
    onMutate: ({ postId, isSaved }) => setSaved(postId, !isSaved),
    onError: (_error, { postId, isSaved }) => setSaved(postId, isSaved),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['savedPosts'] }),
  });
};

/**
 * Hook pour récupérer les hashtags tendance
 */
export const useTrendingHashtags = (
  limit: number = 10,
  hours?: number
): UseQueryResult<HashtagsResponse> => {
  return useQuery({
    queryKey: ['trendingHashtags', limit, hours],
    queryFn: () => postService.getTrendingHashtags({ limit, hours }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Actualiser toutes les 5 minutes
  });
};
